import type { InputHTMLAttributes, ReactElement, ReactNode, Ref } from 'react'
import { useId } from 'react'
import { cn } from '@/lib/cn'

export interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string
  hint?: string
  error?: string
  leadingIcon?: ReactNode
  /** `className` lands on the `<input>` itself; the wrapper is always full-width. */
  ref?: Ref<HTMLInputElement>
}

export function Input({
  label,
  hint,
  error,
  leadingIcon,
  className,
  id,
  'aria-describedby': describedBy,
  ...rest
}: InputProps): ReactElement {
  const generatedId = useId()
  const inputId = id ?? `input-${generatedId}`
  const hintId = `${inputId}-hint`
  const errorId = `${inputId}-error`
  const invalid = Boolean(error)
  // The hint is replaced by the error, so it must not stay in aria-describedby.
  const showHint = Boolean(hint) && !invalid
  const description =
    [describedBy, showHint ? hintId : null, invalid ? errorId : null].filter(Boolean).join(' ') ||
    undefined

  return (
    <div className="w-full">
      {label ? (
        <label htmlFor={inputId} className="mb-1.5 block text-sm font-medium text-fg">
          {label}
        </label>
      ) : null}
      <div className="relative">
        {leadingIcon ? (
          <span
            className="pointer-events-none absolute inset-y-0 left-0 grid w-9 place-items-center text-fg-subtle [&_svg]:size-4"
            aria-hidden="true"
          >
            {leadingIcon}
          </span>
        ) : null}
        <input
          id={inputId}
          aria-invalid={invalid || undefined}
          aria-describedby={description}
          className={cn(
            // Inset in dark, crisp white in light — a field never reads as flat surface.
            'block h-10 w-full rounded-control border bg-surface px-3 text-sm text-fg shadow-sheet transition-colors dark:bg-sunken',
            'placeholder:text-fg-subtle focus:outline-none focus:ring-2',
            'disabled:cursor-not-allowed disabled:bg-sunken disabled:text-fg-subtle disabled:opacity-70',
            invalid
              ? 'border-bad-500 focus:border-bad-500 focus:ring-bad-500/30'
              : 'border-line hover:border-line-strong focus:border-accent focus:ring-accent/30',
            Boolean(leadingIcon) && 'pl-9',
            className
          )}
          {...rest}
        />
      </div>
      {showHint ? (
        <p id={hintId} className="mt-1.5 text-xs text-fg-muted">
          {hint}
        </p>
      ) : null}
      {invalid ? (
        <p id={errorId} className="mt-1.5 text-xs font-medium text-bad-600 dark:text-bad-300">
          {error}
        </p>
      ) : null}
    </div>
  )
}
